import { useEffect, useState } from 'react';
import { Modal, Form, Input, InputNumber, Upload, Button, message, Select, Typography, Divider, Table, Tag } from 'antd';
import { UploadOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { useProducts, Product } from '@hooks';

const { TextArea } = Input;
const { Title } = Typography;
const { Option } = Select;

interface ProductFormModalProps {
  visible: boolean;
  onClose: () => void;
  product: Product | null;
}

interface VariantFormData {
  id?: string; // ID da variante (quando editando)
  sku: string;
  size: string;
  color: string;
  price: number;
  stockQuantity: number;
}

const PREDEFINED_CATEGORIES = [
  'Apparel',
  'Equipment',
  'Tools',
  'Accessories'
];

export const ProductFormModal = ({ visible, onClose, product }: ProductFormModalProps) => {
  const [form] = Form.useForm();
  const [imageFiles, setImageFiles] = useState<UploadFile[]>([]);
  const [documentFiles, setDocumentFiles] = useState<UploadFile[]>([]);
  const [variants, setVariants] = useState<VariantFormData[]>([]);
  const [sizes, setSizes] = useState<string[]>([]);
  const [colors, setColors] = useState<string[]>([]);
  const [newSize, setNewSize] = useState('');
  const [newColor, setNewColor] = useState('');
  const { createProduct, updateProduct, canDeleteVariant } = useProducts();

  const isEditing = !!product;

  useEffect(() => {
    if (visible && product) {
      form.setFieldsValue({
        name: product.name,
        description: product.description,
        category: product.category,
        videoUrl: product.videoUrl,
      });
      
      // Set variants and extract unique sizes/colors
      if (product.variants && product.variants.length > 0) {
        setVariants(product.variants.map(v => ({
          id: v.id, // Incluir o ID da variante
          sku: v.sku || '',
          size: v.size,
          color: v.color,
          price: Number(v.price),
          stockQuantity: v.stockQuantity,
        })));
        
        // Extract unique sizes and colors from variants
        const uniqueSizes = [...new Set(product.variants.map(v => v.size))];
        const uniqueColors = [...new Set(product.variants.map(v => v.color))];
        setSizes(uniqueSizes);
        setColors(uniqueColors);
      } else {
        setVariants([]);
        setSizes([]);
        setColors([]);
      }

      // Load existing documents if editing and product has documents
      if (product.documents && product.documents.length > 0) {
        const bucketName = import.meta.env.VITE_AWS_S3_BUCKET || 'estabrookinc-production';
        const region = import.meta.env.VITE_AWS_S3_BUCKET_REGION || 'us-west-2';
        
        const existingDocs: UploadFile[] = product.documents.map((doc) => {
          const fileUrl = doc.fileUrl.startsWith('http') 
            ? doc.fileUrl 
            : `https://${bucketName}.s3.${region}.amazonaws.com/${doc.fileUrl}`;
          
          return {
            uid: doc.id,
            name: `${doc.name}.pdf`,
            status: 'done' as const,
            url: fileUrl,
            response: { id: doc.id }, // Store document ID for later use
          };
        });
        setDocumentFiles(existingDocs);

        // Set documents metadata in form
        const documentsMetadata = product.documents.map(doc => ({
          name: doc.name,
          description: doc.description || '',
        }));
        form.setFieldsValue({ documentsMetadata });
      } else {
        setDocumentFiles([]);
      }
    } else if (visible) {
      form.resetFields();
      setImageFiles([]);
      setDocumentFiles([]);
      setVariants([]);
      setSizes([]);
      setColors([]);
      setNewSize('');
      setNewColor('');
    }
  }, [visible, product, form]);

  // Apply blue border to upload picture-card elements
  useEffect(() => {
    if (visible) {
      const applyBlueBorder = () => {
        const uploadElements = document.querySelectorAll('.product-upload-blue-border .ant-upload-select-picture-card');
        uploadElements.forEach((el) => {
          const element = el as HTMLElement;
          element.style.border = '1px dashed #00ade6';
          element.style.borderColor = '#00ade6';
        });
      };
      
      // Apply immediately and after a short delay to catch dynamically rendered elements
      applyBlueBorder();
      const timeout = setTimeout(applyBlueBorder, 100);
      
      return () => clearTimeout(timeout);
    }
  }, [visible, imageFiles]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Validate sizes and colors
      if (sizes.length === 0) {
        message.error('Please add at least one size');
        return;
      }
      if (colors.length === 0) {
        message.error('Please add at least one color');
        return;
      }
      
      // Validate variants
      if (variants.length === 0) {
        message.error('Please generate variants and set prices');
        return;
      }
      
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('description', values.description);
      
      if (values.category) formData.append('category', values.category);
      if (values.videoUrl) formData.append('videoUrl', values.videoUrl);

      // Send variants as array - each variant as separate JSON object
      if (variants.length > 0) {
        variants.forEach((variant, index) => {
          // Include the ID if the variant already exists (for updates)
          if (variant.id) {
            formData.append(`variants[${index}][id]`, variant.id);
          }
          formData.append(`variants[${index}][sku]`, variant.sku);
          formData.append(`variants[${index}][size]`, variant.size);
          formData.append(`variants[${index}][color]`, variant.color);
          formData.append(`variants[${index}][price]`, variant.price.toString());
          formData.append(`variants[${index}][stockQuantity]`, variant.stockQuantity.toString());
        });
      }

      // Add images
      imageFiles.forEach((file) => {
        if (file.originFileObj) {
          formData.append('images', file.originFileObj);
        }
      });

      // Add documents
      if (documentFiles.length > 0) {
        documentFiles.forEach((file) => {
          if (file.originFileObj) {
            formData.append('documents', file.originFileObj);
          }
        });
        
        // Use provided metadata or create default from file names
        const documentsMetadata = values.documentsMetadata || documentFiles.map((file) => ({
          name: file.name?.replace('.pdf', '') || 'Document',
          description: '',
        }));
        formData.append('documentsMetadata', JSON.stringify(documentsMetadata));
      }

      if (isEditing) {
        await updateProduct.mutateAsync({ id: product.id, data: formData });
      } else {
        await createProduct.mutateAsync(formData);
      }

      handleClose();
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setImageFiles([]);
    setDocumentFiles([]);
    setVariants([]);
    setSizes([]);
    setColors([]);
    setNewSize('');
    setNewColor('');
    onClose();
  };

  // Size management functions
  const handleAddSize = () => {
    if (newSize.trim() && !sizes.includes(newSize.trim())) {
      setSizes(prev => [...prev, newSize.trim()]);
      setNewSize('');
    }
  };

  const handleRemoveSize = (size: string) => {
    setSizes(prev => prev.filter(s => s !== size));
    // Remove variants with this size
    setVariants(prev => prev.filter(v => v.size !== size));
  };

  // Color management functions
  const handleAddColor = () => {
    if (newColor.trim() && !colors.includes(newColor.trim())) {
      setColors(prev => [...prev, newColor.trim()]);
      setNewColor('');
    }
  };

  const handleRemoveColor = (color: string) => {
    setColors(prev => prev.filter(c => c !== color));
    // Remove variants with this color
    setVariants(prev => prev.filter(v => v.color !== color));
  };

  // Generate all variant combinations
  const generateAllVariants = () => {
    if (sizes.length === 0) {
      message.warning('Please add at least one size first');
      return;
    }
    if (colors.length === 0) {
      message.warning('Please add at least one color first');
      return;
    }

    const newVariants: VariantFormData[] = [];
    
    // Create all combinations of size x color
    for (const size of sizes) {
      for (const color of colors) {
        const existing = variants.find(v => v.size === size && v.color === color);
        newVariants.push(existing || {
          sku: '',
          size,
          color,
          price: 0,
          stockQuantity: 0,
        });
      }
    }
    
    setVariants(newVariants);
    message.success(`Generated ${newVariants.length} variants`);
  };

  const handleAddVariant = () => {
    if (sizes.length === 0 || colors.length === 0) {
      message.warning('Please add sizes and colors first, or use "Generate All Variants"');
      return;
    }
    
    // Find first combination that doesn't exist
    for (const size of sizes) {
      for (const color of colors) {
        const exists = variants.some(v => v.size === size && v.color === color);
        if (!exists) {
          setVariants(prev => [...prev, {
            sku: '',
            size,
            color,
            price: 0,
            stockQuantity: 0,
          }]);
          return;
        }
      }
    }
    
    message.info('All possible combinations already exist');
  };

  const handleRemoveVariant = async (index: number) => {
    const variant = variants[index];
    
    // If the variant has an ID (it already exists in the backend), check whether it can be deleted
    if (variant.id && isEditing) {
      try {
        const result = await canDeleteVariant(variant.id);
        
        if (!result.canDelete) {
          message.error(result.reason || 'Cannot delete this variant');
          return;
        }
      } catch (error) {
        message.error('Error checking if variant can be deleted');
        return;
      }
    }
    
    // If the check passed (or the variant is new), remove it
    setVariants(prev => prev.filter((_, i) => i !== index));
  };

  const handleVariantChange = (index: number, field: keyof VariantFormData, value: any) => {
    setVariants(prev => prev.map((variant, i) => 
      i === index ? { ...variant, [field]: value } : variant
    ));
  };

  return (
    <Modal
      title={
        <Title level={4} className="font-azonix text-primary mb-0">
          {isEditing ? 'Edit Product' : 'Add New Product'}
        </Title>
      }
      open={visible}
      onCancel={handleClose}
      width={800}
      footer={[
        <Button key="cancel" onClick={handleClose} className="btn-single-cut-cancel">
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={createProduct.isPending || updateProduct.isPending}
          onClick={handleSubmit}
          className="btn-single-cut"
        >
          {isEditing ? 'Update' : 'Create'}
        </Button>,
      ]}
      styles={{
        content: { backgroundColor: '#18181b' },
        header: { backgroundColor: '#18181b', borderBottom: '1px solid #3f3f46' },
      }}
    >
      <Form form={form} layout="vertical" className="mt-4">
        {/* Basic Information */}
        <Form.Item
          name="name"
          label={<span className="font-android text-white">Product Name</span>}
          rules={[{ required: true, message: 'Please enter product name' }]}
        >
          <Input placeholder="Enter product name" />
        </Form.Item>

        <Form.Item
          name="description"
          label={<span className="font-android text-white">Description</span>}
          rules={[{ required: true, message: 'Please enter description' }]}
        >
          <TextArea rows={4} placeholder="Enter product description" />
        </Form.Item>

        <Form.Item
          name="category"
          label={<span className="font-android text-white">Category</span>}
        >
          <Select placeholder="Select category" allowClear>
            {PREDEFINED_CATEGORIES.map(cat => (
              <Option key={cat} value={cat}>{cat}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="videoUrl"
          label={<span className="font-android text-white">Video URL (YouTube/Vimeo)</span>}
        >
          <Input placeholder="https://youtube.com/embed/..." />
        </Form.Item>

        {/* Images Upload */}
        <Form.Item
          label={<span className="font-android text-white">Product Images (max 5)</span>}
        >
          <div className="product-upload-blue-border">
            <Upload
              listType="picture-card"
              fileList={imageFiles}
              onChange={({ fileList }) => setImageFiles(fileList)}
              beforeUpload={() => false}
              maxCount={5}
              multiple
            >
              {imageFiles.length < 5 && (
                <div>
                  <PlusOutlined className="text-primary" />
                  <div style={{ marginTop: 8 }} className="text-primary">Upload</div>
                </div>
              )}
            </Upload>
          </div>
        </Form.Item>

        {/* Sizes, Colors & Variants Section */}
        <Divider className="border-zinc-600">
          <span className="font-azonix text-primary">SIZES, COLORS & PRICING (Required)</span>
        </Divider>

        <div className="bg-zinc-800 p-4 border border-zinc-700 mb-4 !rounded-none" style={{ borderRadius: 0 }}>
          <p className="font-coda text-zinc-300 text-sm mb-4">
            Add sizes and colors for this product. Price and stock are defined per variant (size + color combination).
          </p>

          {/* Sizes */}
          <div className="mb-4">
            <label className="font-azonix text-white block mb-2">
              SIZES <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2 mb-2">
              <Input
                value={newSize}
                onChange={(e) => setNewSize(e.target.value)}
                placeholder="e.g., S, M, L, XL"
                className="flex-1"
                onPressEnter={handleAddSize}
              />
              <Button
                icon={<PlusOutlined />}
                onClick={handleAddSize}
                className="btn-single-cut"
              >
                Add
              </Button>
            </div>
            <div className="flex gap-2 flex-wrap">
              {sizes.map((size, index) => (
                <Tag
                  key={index}
                  closable
                  onClose={() => handleRemoveSize(size)}
                  className="font-coda !bg-primary/20 !border-primary !text-black !rounded-none px-3 py-1"
                >
                  {size}
                </Tag>
              ))}
            </div>
            {sizes.length === 0 && (
              <p className="font-coda text-red-400 text-xs mt-2">At least one size is required</p>
            )}
          </div>

          {/* Colors */}
          <div className="mb-4">
            <label className="font-azonix text-white block mb-2">
              COLORS <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2 mb-2">
              <Input
                value={newColor}
                onChange={(e) => setNewColor(e.target.value)}
                placeholder="e.g., Black, Navy, Gray"
                className="flex-1"
                onPressEnter={handleAddColor}
              />
              <Button
                icon={<PlusOutlined />}
                onClick={handleAddColor}
                className="btn-single-cut"
              >
                Add
              </Button>
            </div>
            <div className="flex gap-2 flex-wrap">
              {colors.map((color, index) => (
                <Tag
                  key={index}
                  closable
                  onClose={() => handleRemoveColor(color)}
                  className="font-coda !bg-primary/20 !border-primary !text-black !rounded-none px-3 py-1"
                >
                  {color}
                </Tag>
              ))}
            </div>
            {colors.length === 0 && (
              <p className="font-coda text-red-400 text-xs mt-2">At least one color is required</p>
            )}
          </div>

          {/* Generate Variants Button */}
          {sizes.length > 0 && colors.length > 0 && (
            <div className="mb-4">
              <Button
                onClick={generateAllVariants}
                className="btn-single-cut !text-xs sm:!text-sm !h-auto sm:!h-10 !min-h-8 sm:!min-h-10 !px-3 sm:!px-4 !py-2 sm:!py-0 !whitespace-normal sm:!whitespace-nowrap !text-center"
              >
                Generate All Variants ({sizes.length * colors.length} combinations)
              </Button>
            </div>
          )}

          {/* Variants Table */}
          {variants.length > 0 && (
            <div className="mt-4 [&_*]:!rounded-none" style={{ borderRadius: 0 }}>
              <label className="font-azonix text-white block mb-2">VARIANT PRICES & STOCK</label>
              <div className="overflow-x-auto hide-scrollbar min-w-0 -mx-4 px-4">
                <div className="min-w-[700px]">
                  <Table
                    dataSource={variants.map((v, i) => ({ ...v, key: i }))}
                    pagination={false}
                    size="small"
                    scroll={{ x: 700 }}
                    className="[&_.ant-table-wrapper]:!rounded-none [&_.ant-table-wrapper]:!border-none [&_.ant-table]:!bg-transparent [&_.ant-table]:!rounded-none [&_.ant-table]:!border-none [&_.ant-table-container]:!rounded-none [&_.ant-table-container]:!border-none [&_.ant-table-thead_th]:!bg-zinc-700 [&_.ant-table-thead_th]:!text-white [&_.ant-table-thead_th]:!border-b-zinc-600 [&_.ant-table-thead_th]:!font-coda [&_.ant-table-tbody_td]:!bg-zinc-800/50 [&_.ant-table-tbody_td]:!border-b-zinc-700 [&_.ant-table-tbody_td]:!text-white"
                columns={[
                  {
                    title: <span className="font-coda text-white">SKU</span>,
                    dataIndex: 'sku',
                    key: 'sku',
                    width: 120,
                    render: (value: string, _: any, index: number) => (
                      <Input
                        value={value}
                        onChange={(e) => handleVariantChange(index, 'sku', e.target.value)}
                        placeholder="SKU"
                        className="font-coda"
                        size="small"
                      />
                    ),
                  },
                  {
                    title: <span className="font-coda text-white">Size</span>,
                    dataIndex: 'size',
                    key: 'size',
                    width: 100,
                    render: (text, _, index) => (
                      <Select
                        value={text}
                        onChange={(value) => handleVariantChange(index, 'size', value)}
                        className="w-full font-coda"
                        size="small"
                      >
                        {sizes.map(size => (
                          <Option key={size} value={size}>{size}</Option>
                        ))}
                      </Select>
                    ),
                  },
                  {
                    title: <span className="font-coda text-white">Color</span>,
                    dataIndex: 'color',
                    key: 'color',
                    width: 100,
                    render: (text, _, index) => (
                      <Select
                        value={text}
                        onChange={(value) => handleVariantChange(index, 'color', value)}
                        className="w-full font-coda"
                        size="small"
                      >
                        {colors.map(color => (
                          <Option key={color} value={color}>{color}</Option>
                        ))}
                      </Select>
                    ),
                  },
                  {
                    title: <span className="font-coda text-white">Price ($)</span>,
                    dataIndex: 'price',
                    key: 'price',
                    width: 120,
                    render: (value: number, _: any, index: number) => (
                      <InputNumber
                        value={value}
                        onChange={(val) => handleVariantChange(index, 'price', val || 0)}
                        min={0}
                        step={0.01}
                        precision={2}
                        className="w-full font-coda"
                        size="small"
                      />
                    ),
                  },
                  {
                    title: <span className="font-coda text-white">Stock</span>,
                    dataIndex: 'stockQuantity',
                    key: 'stockQuantity',
                    width: 100,
                    render: (value: number, _: any, index: number) => (
                      <InputNumber
                        value={value}
                        onChange={(val) => handleVariantChange(index, 'stockQuantity', val || 0)}
                        min={0}
                        step={1}
                        precision={0}
                        className="w-full font-coda"
                        size="small"
                      />
                    ),
                  },
                  {
                    title: '',
                    key: 'actions',
                    width: 50,
                    render: (_: any, __: any, index: number) => (
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveVariant(index)}
                        size="small"
                      />
                    ),
                  },
                ]}
                />
                </div>
              </div>
              <Button
                icon={<PlusOutlined />}
                onClick={handleAddVariant}
                className="mt-2 btn-single-cut"
                size="small"
              >
                Add Variant
              </Button>
            </div>
          )}
          
          {/* Show message if no variants generated */}
          {sizes.length > 0 && colors.length > 0 && variants.length === 0 && (
            <div className="mt-4 p-3 bg-primary/10">
              <p className="font-coda text-white text-sm">
                Click "Generate All Variants" to create price entries for each size/color combination.
              </p>
            </div>
          )}
        </div>

        {/* Documents Upload */}
        <Divider className="border-zinc-600" />
        <Form.Item
          label={<span className="font-android text-white">Technical Documents (PDF, max 3)</span>}
        >
          <div className="document-upload-custom [&_.ant-upload-list-item-name]:!text-white [&_.ant-upload-icon]:!text-white [&_.anticon-paper-clip]:!text-white [&_.anticon-paper-clip_svg]:!text-white [&_.ant-upload-list-item-action]:!text-red-500 [&_.ant-upload-list-item-action_button]:!text-red-500 [&_.ant-upload-list-item-action_button]:!bg-transparent [&_.ant-upload-list-item-action_button]:hover:!bg-transparent [&_.ant-upload-list-item-action_button]:hover:!border-transparent [&_.ant-upload-list-item-action_button_.anticon]:!text-red-500 [&_.ant-upload-list-item-action_button_.anticon_svg]:!text-red-500 [&_.ant-upload-list-item-action_button_.anticon_svg]:!fill-red-500 [&_.ant-upload-list-item-action_button:hover_.anticon_svg]:!drop-shadow-[0_2px_4px_rgba(239,68,68,0.4)]">
            <Upload
              fileList={documentFiles}
              onChange={({ fileList }) => setDocumentFiles(fileList)}
              beforeUpload={() => false}
              maxCount={3}
              accept=".pdf"
            >
              <Button icon={<UploadOutlined />} className="btn-single-cut">Upload PDF</Button>
            </Upload>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

